import { useEffect, useMemo, useState } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { useAppSelector, useLocaleDateFormat, useTrack } from '../hooks';
import { dayjs, requestDeleteItem } from '../smi-bridge';
import { LocaleText } from '../LocaleText';
import { Footer } from '../components/Footer';
import type { SkipNode } from '../types';

const units = ['day', 'week', 'month'];

export const SkipStep = ({
  // step unused for now, but declared for consistency with other steps
  step: _step
}: {
  step: SkipNode;
}) => {
  const { dispatch } = useStepContext();
  const track = useTrack();
  const subscription = useSubscriptionContext();
  const orderItem = useAppSelector(state => state.order_items.find(oi => oi.subscription === subscription.public_id));
  const order = useAppSelector(state => state.orders.find(o => o.public_id === orderItem?.order));
  const product = useAppSelector(state => state.products.find(p => p.external_product_id === subscription?.product));

  const nextOrderDate = useMemo(
    () => (order?.place ? dayjs(order.place).add(subscription.every, units[subscription.every_period - 1]) : null),
    [order?.place, subscription.every, subscription.every_period]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const formatDate = useLocaleDateFormat();

  useEffect(() => {
    track('subscriptioncancel_skip_shown', {
      subscriptionId: subscription.public_id
    });
  }, [subscription.public_id, track]);

  async function handleSkip() {
    if (!orderItem) {
      console.error('Cannot skip: order item not found');
      return;
    }

    try {
      setIsSubmitting(true);
      await requestDeleteItem(orderItem.public_id);
      track('subscriptioncancel_completed_saved_skipped', {
        subscriptionId: subscription.public_id
      });
      dispatch({ type: 'accepted' });
    } catch (error) {
      console.error('Error deleting order item:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!orderItem || !nextOrderDate || !product) {
    return <p>An error has occurred. Please try again.</p>;
  }

  return (
    <div className="og-skip-step">
      <p className="og-skip-description">
        <LocaleText t="description" parameters={{ productName: product.name }} />
      </p>

      <div className="og-next-order-date">
        <dl>
          <dt className="text-sm">
            <LocaleText t="newOrderDate" />
          </dt>
          <dd className="text-xl fw-bold">{formatDate(nextOrderDate)}</dd>
        </dl>
        <button disabled={isSubmitting} onClick={handleSkip} className="og-button" data-variant="primary">
          <LocaleText t="acceptButton" />
        </button>
      </div>

      <Footer />
    </div>
  );
};
